import { useCallback, useState } from "react";
import type { StaffRole } from "../api/types";
import { useDismiss } from "../hooks";
import { RoleChip } from "./StatusChip";

const ROLES: StaffRole[] = ["rm", "admin"];

/**
 * Role picker as a menu of chips.
 *
 * A native <select> paints its options with the OS, as bare lowercase text, so
 * the one place a role is CHOSEN was the one place it did not look like the
 * chip that same role becomes everywhere else. This renders the options as the
 * chips they will be.
 */
export function RoleMenu({
  value,
  onChange,
  disabled,
  labelledBy,
}: {
  value: StaffRole;
  onChange: (role: StaffRole) => void;
  disabled?: boolean;
  labelledBy?: string;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);

  function pick(role: StaffRole) {
    onChange(role);
    setOpen(false);
  }

  function onTriggerKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
    }
  }

  return (
    <div className="menu-wrap" ref={ref}>
      <button
        type="button"
        className="menu-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        {...(labelledBy ? { "aria-labelledby": labelledBy } : {})}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        onKeyDown={onTriggerKeyDown}
      >
        <RoleChip role={value} />
        <span className="menu-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open ? (
        <div className="menu" role="listbox" aria-label="Role">
          {ROLES.map((role) => (
            <button
              key={role}
              type="button"
              role="option"
              aria-selected={role === value}
              className="menu-item"
              onClick={() => pick(role)}
            >
              <span className="menu-check" aria-hidden="true">
                {role === value ? "✓" : ""}
              </span>
              <RoleChip role={role} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
